import { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Loader2, Check, Send } from "lucide-react";

const haptic = () => navigator.vibrate?.(8);

export const DailyReflection = ({ onComplete }) => {
  const [ref, setRef] = useState(null);
  const [mood, setMood] = useState("");
  const [loading, setLoading] = useState(true);
  const [done, setDone] = useState(false);

  const fetchDaily = async (m) => {
    setLoading(true);
    try {
      const { data } = await api.post("/alignment/daily", { mood: m || null });
      setRef(data.reflection);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDaily(); /* eslint-disable-next-line */ }, []);

  const personalize = (e) => {
    e.preventDefault();
    if (!mood.trim()) return;
    haptic();
    fetchDaily(mood.trim());
  };

  const complete = async () => {
    haptic();
    try {
      const { data } = await api.post("/alignment/complete", { kind: "reflection" });
      setDone(true);
      toast.success("Reflection noted. Your streak grows.");
      onComplete?.(data);
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  return (
    <div
      data-testid="daily-reflection"
      className="rounded-2xl border border-teal-300/20 bg-white/[0.03] backdrop-blur-md p-7 sm:p-9"
      style={{ boxShadow: "0 0 60px rgba(45,212,191,0.08)" }}
    >
      <div className="flex items-center gap-2 mb-4 text-teal-300">
        <Sparkles className="w-5 h-5" />
        <span className="text-sm tracking-[0.25em] uppercase">Today&rsquo;s Reflection</span>
      </div>

      {loading ? (
        <div className="flex items-center gap-3 text-slate-400 py-6"><Loader2 className="w-5 h-5 animate-spin" /> Composing a moment of stillness…</div>
      ) : ref ? (
        <div data-testid="reflection-content">
          <h2 className="font-head font-700 text-2xl sm:text-3xl text-slate-50 mb-3">{ref.title}</h2>
          <p className="text-base sm:text-lg text-slate-300 leading-relaxed mb-5">{ref.reflection}</p>
          <div className="flex items-start gap-3 rounded-xl border border-teal-300/20 bg-teal-300/5 p-4">
            <Check className="w-5 h-5 text-teal-300 mt-0.5 shrink-0" />
            <p className="text-sm text-teal-100/90"><span className="uppercase tracking-wider text-teal-300 text-xs">Practice · </span>{ref.practice}</p>
          </div>
        </div>
      ) : null}

      <form onSubmit={personalize} className="mt-6 flex flex-col sm:flex-row gap-3">
        <input
          data-testid="mood-input"
          value={mood}
          onChange={(e) => setMood(e.target.value)}
          placeholder="How are you feeling right now?"
          className="flex-1 rounded-xl bg-white/5 border border-white/10 px-4 py-3 text-slate-100 text-sm placeholder:text-slate-500 focus:border-teal-300/50 focus:ring-1 focus:ring-teal-300/40 outline-none transition-colors duration-300"
        />
        <button
          data-testid="personalize-btn"
          type="submit"
          disabled={loading || !mood.trim()}
          className="inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-medium bg-teal-400/90 text-slate-900 hover:bg-teal-300 transition-colors duration-300 disabled:opacity-50"
        >
          <Send className="w-4 h-4" /> Attune
        </button>
      </form>

      <button
        data-testid="complete-reflection-btn"
        onClick={complete}
        disabled={done}
        className="mt-4 text-sm text-teal-300/80 hover:text-teal-200 transition-colors duration-300 disabled:opacity-60 inline-flex items-center gap-2"
      >
        <Check className="w-4 h-4" /> {done ? "Marked complete" : "Mark this practice complete"}
      </button>
    </div>
  );
};
